import { ArrowLeft, Globe, TrendingUp, Building2, Users, Briefcase, CheckCircle, Mail, Linkedin } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useEffect } from 'react';

function Careers() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const benefits = [
    {
      icon: Globe,
      title: "International Exposure",
      titlePt: "Exposição Internacional",
      description: "Work on projects involving cross-border tax structures and global business operations.",
      descriptionPt: "Trabalhe em projetos envolvendo estruturas tributárias internacionais e operações comerciais globais."
    },
    {
      icon: TrendingUp,
      title: "Professional Growth",
      titlePt: "Crescimento Profissional",
      description: "Join a firm focused on international advisory services and global expansion.",
      descriptionPt: "Junte-se a uma firma focada em serviços de consultoria internacional e expansão global."
    },
    {
      icon: Building2,
      title: "High-Level Clients",
      titlePt: "Clientes de Alto Nível",
      description: "Our work supports multinational corporations, private investors, and international entrepreneurs.",
      descriptionPt: "Nosso trabalho apoia corporações multinacionais, investidores privados e empreendedores internacionais."
    },
    {
      icon: Users,
      title: "Collaborative Environment",
      titlePt: "Ambiente Colaborativo",
      description: "Work alongside professionals with experience in tax advisory, compliance, and international consulting.",
      descriptionPt: "Trabalhe ao lado de profissionais com experiência em consultoria tributária, compliance e consultoria internacional."
    }
  ];

  const positions = [
    {
      title: "Operational & Administrative Assistant",
      titlePt: "Assistente Operacional e Administrativo",
      description: "This role supports internal operations, coordination, reporting, and documentation essential to the firm's daily activities.",
      descriptionPt: "Esta função apoia operações internas, coordenação, relatórios e documentação essenciais às atividades diárias da firma.",
      responsibilities: [
        "Coordinate internal meetings, schedules, and communications",
        "Support employee onboarding and personnel administration processes",
        "Manage document organization, archiving, and retention policies",
        "Assist with procurement processes and vendor coordination",
        "Monitor operational expenses and assist with budget tracking",
        "Prepare invoices and administrative documentation for clients"
      ],
      qualifications: [
        "Minimum 3 years of experience in administrative or operational support roles",
        "Strong organizational and communication skills",
        "Ability to manage multiple priorities with attention to detail",
        "Professional interpersonal skills and ability to collaborate across teams"
      ]
    },
    {
      title: "Business Development & Relationship Analyst",
      titlePt: "Analista de Desenvolvimento de Negócios e Relacionamento",
      description: "This role focuses on identifying growth opportunities and building relationships with companies operating across the United States and Brazil.",
      descriptionPt: "Esta função foca em identificar oportunidades de crescimento e construir relacionamentos com empresas operando entre Estados Unidos e Brasil.",
      responsibilities: [
        "Identify and research companies with cross-border tax exposure",
        "Develop relationships with multinational corporations and international investors",
        "Act as liaison between clients and BTACC advisory specialists",
        "Monitor international tax developments and regulatory trends",
        "Analyze client feedback and identify opportunities for service improvement",
        "Support account growth through strategic client engagement"
      ],
      qualifications: [
        "Bachelor's degree in Business, Finance, Economics, Accounting, or related field",
        "3–5 years of experience in business development, consulting, or financial services",
        "Strong communication and relationship management skills"
      ]
    },
    {
      title: "Tax Specialist – Brazilian Corporate Tax",
      titlePt: "Especialista Tributário – Impostos Corporativos Brasileiros",
      description: "This position supports cross-border tax advisory engagements involving U.S. companies operating in Brazil and Brazilian tax structures connected to international investments.",
      descriptionPt: "Esta posição apoia engajamentos de consultoria tributária transfronteiriça envolvendo empresas americanas operando no Brasil e estruturas tributárias brasileiras conectadas a investimentos internacionais.",
      responsibilities: [
        "Advise on Brazilian tax regimes including IRPJ, CSLL, PIS/COFINS, ICMS, and ISS",
        "Analyze tax implications of establishing Brazilian entities (LTDA, S.A., branch structures)",
        "Evaluate withholding taxes on international remittances",
        "Assess permanent establishment risks and compliance obligations",
        "Analyze transfer pricing exposure under Brazilian rules",
        "Support preparation and review of corporate tax filings and SPED reporting"
      ],
      qualifications: [
        "Bachelor's degree in Accounting, Law, or Economics",
        "Postgraduate specialization in Tax preferred",
        "4–8 years of experience in Brazilian corporate taxation"
      ]
    }
  ];

  const hiringSteps = [
    "Application review",
    "Initial interview with management",
    "Technical or role-specific discussion",
    "Final interview and offer"
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header Navigation */}
      <header className="fixed top-0 left-0 right-0 bg-btacc-blue z-50 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-20">
            <Link to="/" className="flex-shrink-0">
              <h1 className="text-2xl font-montserrat font-bold text-white">
                BT<span className="text-btacc-gold">ACC</span>
              </h1>
              <p className="text-xs text-btacc-light font-inter -mt-1">Brazil Tax Advisory & Compliance Center</p>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-b from-btacc-blue to-btacc-secondary">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Link to="/" className="inline-flex items-center text-btacc-light hover:text-white transition mb-8">
            <ArrowLeft size={20} className="mr-2" />
            Back to Home
          </Link>
          
          <h1 className="text-4xl md:text-5xl font-montserrat font-bold text-white mb-6">
            Careers at BTACC
          </h1>
          <p className="text-xl text-btacc-gold font-montserrat font-semibold mb-4">
            Build the future of cross-border tax advisory
          </p>
          <p className="text-lg text-btacc-light font-inter max-w-3xl mx-auto">
            BTACC is an international advisory firm dedicated to helping companies and investors navigate the complex tax and regulatory landscape between the United States and Brazil.
          </p>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-16 bg-btacc-light">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white p-8 rounded-lg shadow-sm">
            <p className="text-btacc-graphite font-inter leading-relaxed mb-4">
              Our mission is to provide clear, practical, and reliable tax advisory solutions for businesses operating across borders.
            </p>
            <p className="text-btacc-graphite font-inter leading-relaxed mb-4">
              As international commerce continues to expand, the need for professionals who understand tax systems, compliance frameworks, and global regulatory environments is greater than ever.
            </p>
            <p className="text-btacc-graphite font-inter leading-relaxed italic text-sm">
              Nossa missão é fornecer soluções de consultoria tributária claras, práticas e confiáveis para empresas operando através de fronteiras.
            </p>
          </div>
        </div>
      </section>

      {/* Why Join BTACC */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-montserrat font-bold text-btacc-blue text-center mb-4">
            Why Join BTACC
          </h2>
          <p className="text-center text-btacc-secondary font-inter italic mb-12">
            Por que se juntar à BTACC
          </p>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit) => (
              <div key={benefit.title} className="text-center p-6 rounded-lg border border-gray-100 hover:shadow-lg transition">
                <div className="w-16 h-16 bg-btacc-light rounded-full flex items-center justify-center mx-auto mb-4">
                  <benefit.icon className="text-btacc-gold" size={32} />
                </div>
                <h3 className="text-lg font-montserrat font-bold text-btacc-blue mb-2">
                  {benefit.title}
                </h3>
                <p className="text-btacc-graphite font-inter text-sm mb-2">
                  {benefit.description}
                </p>
                <p className="text-btacc-secondary font-inter text-xs italic">
                  {benefit.descriptionPt}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Current Open Positions */}
      <section className="py-20 bg-btacc-light">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-montserrat font-bold text-btacc-blue text-center mb-4">
            Current Open Positions
          </h2>
          <p className="text-center text-btacc-secondary font-inter italic mb-12">
            Vagas Abertas
          </p>
          
          <div className="space-y-8">
            {positions.map((position) => (
              <div key={position.title} className="bg-white rounded-lg shadow-sm overflow-hidden">
                <div className="bg-btacc-blue p-6">
                  <div className="flex items-center">
                    <Briefcase className="text-btacc-gold mr-3" size={24} />
                    <div>
                      <h3 className="text-xl font-montserrat font-bold text-white">
                        {position.title}
                      </h3>
                      <p className="text-btacc-light font-inter text-sm italic">
                        {position.titlePt}
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="p-6">
                  <p className="text-btacc-graphite font-inter mb-2">
                    {position.description}
                  </p>
                  <p className="text-btacc-secondary font-inter text-sm italic mb-6">
                    {position.descriptionPt}
                  </p>
                  
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="font-montserrat font-semibold text-btacc-blue mb-3">
                        Key Responsibilities
                      </h4>
                      <ul className="space-y-2">
                        {position.responsibilities.map((resp) => (
                          <li key={resp} className="flex items-start text-sm text-btacc-graphite font-inter">
                            <CheckCircle className="text-btacc-gold mr-2 mt-0.5 flex-shrink-0" size={16} />
                            {resp}
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    <div>
                      <h4 className="font-montserrat font-semibold text-btacc-blue mb-3">
                        Qualifications
                      </h4>
                      <ul className="space-y-2">
                        {position.qualifications.map((qual) => (
                          <li key={qual} className="flex items-start text-sm text-btacc-graphite font-inter">
                            <CheckCircle className="text-btacc-gold mr-2 mt-0.5 flex-shrink-0" size={16} />
                            {qual}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Hiring Process */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-montserrat font-bold text-btacc-blue text-center mb-4">
            Our Hiring Process
          </h2>
          <p className="text-center text-btacc-secondary font-inter italic mb-8">
            Nosso Processo de Contratação
          </p>
          
          <p className="text-center text-btacc-graphite font-inter mb-12">
            Our hiring process is designed to identify professionals who share our commitment to excellence and international advisory services.
          </p>
          
          <div className="flex flex-col md:flex-row justify-center items-center gap-4 mb-8">
            {hiringSteps.map((step, index) => (
              <div key={step} className="flex items-center">
                <div className="flex items-center justify-center w-10 h-10 bg-btacc-gold text-btacc-blue font-montserrat font-bold rounded-full">
                  {index + 1}
                </div>
                <span className="ml-3 text-btacc-graphite font-inter text-sm md:text-base">
                  {step}
                </span>
                {index < hiringSteps.length - 1 && (
                  <div className="hidden md:block w-8 h-0.5 bg-btacc-light ml-4"></div>
                )}
              </div>
            ))}
          </div>
          
          <p className="text-center text-btacc-secondary font-inter text-sm italic">
            Selected candidates will be contacted directly by our team.
          </p>
        </div>
      </section>

      {/* Apply Section */}
      <section className="py-20 bg-btacc-blue">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-montserrat font-bold text-white mb-6">
            Apply
          </h2>
          
          <p className="text-btacc-light font-inter mb-8 max-w-2xl mx-auto">
            If you are interested in joining BTACC and contributing to the development of an international advisory firm focused on cross-border tax and compliance, we encourage you to apply.
          </p>
          
          <p className="text-white font-inter mb-6">
            Please send your resume to:
          </p>
          
          <div className="flex flex-col sm:flex-row justify-center items-center gap-4 mb-8">
            <a 
              href="mailto:contact@btacc.info" 
              className="inline-flex items-center bg-btacc-gold text-btacc-blue px-8 py-4 rounded-md font-montserrat font-semibold hover:bg-white transition"
            >
              <Mail className="mr-2" size={20} />
              contact@btacc.info
            </a>
            
            <span className="text-btacc-light">or</span>
            
            <a 
              href="https://www.linkedin.com/company/brazil-tax-advisory-compliance-center" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center bg-white text-btacc-blue px-8 py-4 rounded-md font-montserrat font-semibold hover:bg-btacc-gold transition"
            >
              <Linkedin className="mr-2" size={20} />
              Apply on LinkedIn
            </a>
          </div>
        </div>
      </section>

      {/* Equal Opportunity */}
      <section className="py-12 bg-btacc-secondary">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="text-lg font-montserrat font-semibold text-white mb-4">
            Equal Opportunity Employer
          </h3>
          <p className="text-btacc-light font-inter text-sm">
            BTACC is an equal opportunity employer and values diversity in the workplace. We do not discriminate based on race, religion, gender, national origin, age, disability, or any other protected status.
          </p>
          <p className="text-btacc-light font-inter text-xs italic mt-2">
            A BTACC é um empregador de oportunidades iguais e valoriza a diversidade no ambiente de trabalho.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-btacc-blue text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Link to="/" className="inline-block mb-4">
            <h2 className="text-2xl font-montserrat font-bold">
              BT<span className="text-btacc-gold">ACC</span>
            </h2>
          </Link>
          <div className="text-sm text-btacc-light mb-4">
            <p>Brazil Tax Advisory & Compliance Center</p>
            <p>320 Decker Drive, Irving, Texas 75062</p>
          </div>
          <p className="text-sm text-btacc-light">
            © {new Date().getFullYear()} BTACC — All rights reserved. / Todos os direitos reservados.
          </p>
        </div>
      </footer>
    </div>
  );
}

export default Careers;
